import { useEffect } from 'react';

/**
 * Custom hook to manage SEO meta tags via direct DOM manipulation.
 * This is more reliable than react-helmet-async with React 19.
 * 
 * @param {Object} seoData - SEO data object with title, description, keywords, etc.
 * @param {Object} options - Additional options for OG and Twitter tags
 */
export function useSEO(seoData, options = {}) {
  useEffect(() => {
    if (!seoData) return;

    const {
      seo_title,
      seo_description,
      seo_keywords,
      seo_robots,
      canonical_url
    } = seoData;

    const {
      og_type = 'website',
      og_image,
      twitter_card = 'summary_large_image'
    } = options;

    // Helper to update or create meta tag
    const setMetaTag = (selector, content, attribute = 'name') => {
      if (!content) return;
      
      let meta = document.querySelector(`meta[${attribute}="${selector}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, selector);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to update or create link tag
    const setLinkTag = (rel, href) => {
      if (!href) return;
      
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Set document title
    if (seo_title) {
      document.title = seo_title;
    }

    // Set meta tags
    setMetaTag('description', seo_description);
    setMetaTag('keywords', seo_keywords);
    setMetaTag('robots', seo_robots || 'index, follow');

    // Set Open Graph tags
    setMetaTag('og:title', seo_title, 'property');
    setMetaTag('og:description', seo_description, 'property');
    setMetaTag('og:type', og_type, 'property');
    if (canonical_url) {
      setMetaTag('og:url', canonical_url, 'property');
    }
    if (og_image) {
      setMetaTag('og:image', og_image, 'property');
    }

    // Set Twitter Card tags
    setMetaTag('twitter:card', twitter_card);
    setMetaTag('twitter:title', seo_title);
    setMetaTag('twitter:description', seo_description);

    // Set canonical URL
    if (canonical_url) {
      setLinkTag('canonical', canonical_url);
    }

    // Cleanup function to remove dynamically added tags on unmount
    return () => {
      // We don't remove tags on unmount to avoid flickering
      // The next page will simply update them
    };
  }, [seoData, options]);
}

export default useSEO;
